import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { EditHistoryEntry } from '../types';

interface EditHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  history: EditHistoryEntry[];
}

const EditHistoryModal = ({ isOpen, onClose, history }: EditHistoryModalProps) => {
  const [searchTerm, setSearchTerm] = useState('');

  if (!isOpen) return null;

  const formatDateTime = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    }).format(date);
  };

  const filteredHistory = history.filter(entry =>
    entry.field.toLowerCase().includes(searchTerm.toLowerCase()) ||
    entry.editedBy.toLowerCase().includes(searchTerm.toLowerCase()) ||
    entry.oldValue.toLowerCase().includes(searchTerm.toLowerCase()) ||
    entry.newValue.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-1200 p-4">
      <div className="bg-card rounded-md shadow-elevation-2 w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="p-6 border-b border-border flex items-center justify-between">
          <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
            <Icon name="History" size={24} />
            Edit History
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-sm hover:bg-muted transition-interactive"
            aria-label="Close modal"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="p-6 border-b border-border">
          <div className="relative">
            <Icon
              name="Search"
              size={18}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
            />
            <input
              type="text"
              placeholder="Search edit history..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-background border border-border rounded-sm text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {filteredHistory.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                <Icon name="FileText" size={32} className="text-muted-foreground" />
              </div>
              <p className="text-muted-foreground">
                {searchTerm ? 'No matching edit history found' : 'No edit history available'}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredHistory.map((entry) => (
                <div
                  key={entry.id}
                  className="p-4 bg-muted rounded-sm border border-border"
                >
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <Icon name="Edit" size={20} color="var(--color-primary)" />
                      </div>
                      <div>
                        <div className="text-sm font-medium text-foreground">
                          {entry.editedBy}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {formatDateTime(entry.timestamp)}
                        </div>
                      </div>
                    </div>
                    <span className="px-2 py-1 bg-primary/10 text-primary text-xs font-medium rounded-sm">
                      {entry.field}
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                    <div className="p-3 bg-background rounded-sm">
                      <div className="text-xs text-muted-foreground mb-1">Previous Value</div>
                      <div className="text-sm text-foreground font-medium">
                        {entry.oldValue}
                      </div>
                    </div>
                    <div className="p-3 bg-background rounded-sm">
                      <div className="text-xs text-muted-foreground mb-1">New Value</div>
                      <div className="text-sm text-foreground font-medium">
                        {entry.newValue}
                      </div>
                    </div>
                  </div>

                  {entry.reason && (
                    <div className="flex items-start gap-2 text-sm">
                      <Icon name="MessageSquare" size={16} className="text-muted-foreground flex-shrink-0 mt-0.5" />
                      <div>
                        <span className="text-muted-foreground">Reason: </span>
                        <span className="text-foreground">{entry.reason}</span>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="p-6 border-t border-border flex justify-end">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EditHistoryModal;